import React, { useEffect, useState, useCallback } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { OutlinedInput, Button, Box, Modal, IconButton } from '@mui/material';
import axios from 'lib/axios';
import searchIcon from '../../assets/static_images/searchicon.svg';
import { Avatar } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import tikcircle from '../../assets/static_images/tikcircle.svg';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import debounce from 'lodash/debounce';
import { FiX } from 'react-icons/fi';

/**
 * UserSelectionModal Component
 *
 * A modal component for selecting users with filtering capabilities
 *
 * @param {boolean} open - Controls whether the modal is open/visible
 * @param {function} setOpen - Function to set the modal open state (true/false)
 * @param {function} onUsersSelected - Callback function called when users are selected and confirmed. Receives array of selected user objects
 * @param {array} previouslySelectedUsers - Array of user objects that should be pre-selected when modal opens (default: [])
 * @param {string} title - Title text displayed at the top of the modal (default: 'Select Users to Compare')
 * @param {string} buttonText - Text for the confirmation button (default: 'Compare Selected Users')
 * @param {array} excludedUsers - Array of user objects that should be hidden/excluded from the selection list (default: [])
 * @param {array} includeOnlyUsers - Array of user objects that should be the ONLY users shown (filters to only these users, default: null means show all)
 *
 * @example
 * // Basic usage
 * <UserSelectionModal
 *   open={isModalOpen}
 *   setOpen={setIsModalOpen}
 *   onUsersSelected={(selectedUsers) => console.log('Selected users:', selectedUsers)}
 * />
 *
 * @example
 * // With excluded users (hide specific users)
 * const usersToHide = [{ user_id: 1, first_name: 'John' }, { user_id: 2, first_name: 'Jane' }];
 * <UserSelectionModal
 *   open={isModalOpen}
 *   setOpen={setIsModalOpen}
 *   onUsersSelected={handleUserSelection}
 *   excludedUsers={usersToHide}
 *   title="Select Team Members"
 *   buttonText="Add to Team"
 * />
 *
 * @example
 * // With include only users (show only specific users)
 * const allowedUsers = [{ user_id: 3, first_name: 'Alice' }, { user_id: 4, first_name: 'Bob' }];
 * <UserSelectionModal
 *   open={isModalOpen}
 *   setOpen={setIsModalOpen}
 *   onUsersSelected={handleUserSelection}
 *   includeOnlyUsers={allowedUsers}
 * />
 */
const UserSelectionModal = ({
    open,
    setOpen,
    onUsersSelected,
    previouslySelectedUsers = [],
    title,
    buttonText,
    excludedUsers = [],
    includeOnlyUsers = null
}) => {
    const [selectedUserIds, setSelectedUserIds] = useState(
        previouslySelectedUsers.length > 0 ? previouslySelectedUsers.map((user) => user?.user_id) : []
    );
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [query, setQuery] = useState('');
    const [departments, setDepartments] = useState([]);
    const [departmentDivisions, setDepartmentDivisions] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [selectedDivisionId, setSelectedDivisionId] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branches, setBranches] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const resetData = () => {
        setSelectedUserIds([]);
        setFilteredUsers([]);
        setQuery('');
        setDepartments([]);
        setDepartmentDivisions([]);
        setSelectedDepartmentId('');
        setSelectedDivisionId('');
    };

    const fetchDeptAndDivByBranch = useCallback(async () => {
        try {
            const { data } = await axios.post('/branch/get-department-and-division-by-branch', { branch_id: selectedBranch });
            if (data.success) {
                setDepartments(data.data.departments || []);
                setDepartmentDivisions(data.data.department_divisions || []);
            }
        } catch (error) {
            setDepartments([]);
            setDepartmentDivisions([]);
            console.log(error);
        }
    }, [selectedBranch]);

    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            let url = '/users?';
            if (selectedBranch) url += `branch_id=${selectedBranch}&`;
            if (selectedDepartmentId) url += `department_id=${selectedDepartmentId}&`;
            if (selectedDivisionId) url += `department_division_id=${selectedDivisionId}&`;

            const { data } = await axios.get(url);
            if (data.success) {
                setFilteredUsers(data.users);
            }
        } catch (err) {
            setFilteredUsers([]);
            console.log(err);
        } finally {
            setLoadingUsers(false);
        }
    }, [selectedBranch, selectedDepartmentId, selectedDivisionId]);

    const debouncedFetchUsers = useCallback(
        debounce(async (searchValue) => {
            setLoadingUsers(true);
            try {
                let url = '/users?';
                if (selectedBranch) url += `branch_id=${selectedBranch}&`;
                if (selectedDepartmentId) url += `department_id=${selectedDepartmentId}&`;
                if (selectedDivisionId) url += `department_division_id=${selectedDivisionId}&`;
                if (searchValue?.trim()) url += `s=${encodeURIComponent(searchValue.trim())}&`;

                const { data } = await axios.get(url);
                if (data.success) {
                    setFilteredUsers(data.users);
                }
            } catch (err) {
                setFilteredUsers([]);
                console.log(err);
            } finally {
                setLoadingUsers(false);
            }
        }, 500),
        [selectedBranch, selectedDepartmentId, selectedDivisionId]
    );

    useEffect(() => {
        return () => {
            debouncedFetchUsers.cancel();
        };
    }, [debouncedFetchUsers]);

    const fetchBranches = async () => {
        try {
            const { data } = await axios.get('/branch/all');
            if (data.success) {
                setBranches(data.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchBranches();
        fetchDeptAndDivByBranch();
    }, [fetchUsers, fetchDeptAndDivByBranch]);

    useEffect(() => {
        fetchUsers();
    }, [selectedBranch, selectedDepartmentId, selectedDivisionId, fetchUsers, open]);

    useEffect(() => {
        fetchDeptAndDivByBranch();
    }, [selectedBranch, fetchDeptAndDivByBranch]);

    /**
     * Get the list of users that should be visible in the modal
     *
     * Filtering logic:
     * 1. Combines previously selected users with filtered users from API
     * 2. Removes duplicates based on user_id
     * 3. Excludes users specified in excludedUsers prop (removes them from list)
     * 4. If includeOnlyUsers is provided, only shows those specific users
     *
     * @returns {array} Array of user objects that should be displayed
     */
    const getVisibleUsers = () => {
        // Combine previously selected users with filtered users, removing duplicates
        const allUsers = [...previouslySelectedUsers, ...filteredUsers];
        const uniqueUsers = Array.from(new Map(allUsers.map((user) => [user.user_id, user])).values());

        // Filter out excluded users (hide specific users from the list)
        let visibleUsers = uniqueUsers.filter((user) => !excludedUsers.some((excluded) => excluded.user_id === user.user_id));

        // If includeOnlyUsers is provided, filter to only show those users (whitelist approach)
        if (includeOnlyUsers && includeOnlyUsers.length > 0) {
            visibleUsers = visibleUsers.filter((user) => includeOnlyUsers.some((allowedUser) => allowedUser.user_id === user.user_id));
        }

        return visibleUsers;
    };

    const handleClose = () => {
        resetData();
        setOpen(false);
    };

    const handleConfirm = () => {
        const selectedUsers = getVisibleUsers().filter((user) => selectedUserIds.includes(user.user_id));
        onUsersSelected(selectedUsers);
        handleClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="user-selection-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Card
                sx={{
                    p: 1,
                    borderRadius: 1,
                    width: { xs: '90vw', md: '40vw' },
                    maxHeight: '90vh',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <CardContent sx={{ flex: 1, overflowY: 'auto', pb: '90px' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'text.secondary',
                            bgcolor: 'grey.100',
                            borderRadius: '50%',
                            '&:hover': { bgcolor: 'grey.300' },
                            zIndex: 2
                        }}
                        size="small"
                    >
                        <FiX size={17} />
                    </IconButton>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" color="primary">
                            {title || 'Select Users to Compare'}
                        </Typography>
                    </Box>

                    <Box display="flex" gap={2} mb={2}>
                        <Box display="flex" flex={1}>
                            <OutlinedInput
                                value={query}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setQuery(value);
                                    if (!value) {
                                        fetchUsers();
                                    } else {
                                        debouncedFetchUsers(value);
                                    }
                                }}
                                placeholder="Search For User"
                                fullWidth
                            />
                        </Box>
                        <Button
                            variant="contained"
                            onClick={() => {
                                debouncedFetchUsers(query);
                            }}
                        >
                            <Box component="img" src={searchIcon} alt="search" />
                        </Button>
                    </Box>

                    <Box display="flex" gap={2} mb={2}>
                        <FormControl fullWidth>
                            <Select
                                displayEmpty
                                value={selectedBranch}
                                onChange={(e) => {
                                    setSelectedBranch(e.target.value);
                                    setSelectedDepartmentId('');
                                    setSelectedDivisionId('');
                                }}
                                renderValue={(selected) => {
                                    if (!selected) return 'Select Branch';
                                    const branch = branches.find((b) => b.branch_id === selected);
                                    return branch ? branch.branch_name : 'Select Branch';
                                }}
                            >
                                <MenuItem value="">All Branches</MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.branch_id} value={branch.branch_id}>
                                        {branch.branch_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <Select
                                displayEmpty
                                value={selectedDepartmentId}
                                onChange={(e) => {
                                    setSelectedDepartmentId(e.target.value);
                                    setSelectedDivisionId('');
                                }}
                                renderValue={(selected) => {
                                    if (!selected) return 'Select Department';
                                    const dept = departments.find((d) => d.department_id === selected);
                                    return dept ? dept.department_name || dept.name : 'Select Department';
                                }}
                            >
                                <MenuItem value="">All Departments</MenuItem>
                                {departments.map((dept) => (
                                    <MenuItem key={dept.department_id} value={dept.department_id}>
                                        {dept.department_name || dept.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <Select
                                displayEmpty
                                value={selectedDivisionId}
                                onChange={(e) => {
                                    setSelectedDivisionId(e.target.value);
                                }}
                                renderValue={(selected) => {
                                    if (!selected) return 'Select Division';
                                    const div = departmentDivisions.find((d) => d.department_division_id === selected);
                                    return div ? div.division_name || div.name : 'Select Division';
                                }}
                            >
                                <MenuItem value="">All Divisions</MenuItem>
                                {departmentDivisions.map((div) => (
                                    <MenuItem key={div.department_division_id} value={div.department_division_id}>
                                        {div.division_name || div.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" color="primary">
                            Users ({getVisibleUsers().length})
                        </Typography>
                        {getVisibleUsers().length > 0 && (
                            <Button
                                variant="text"
                                size="small"
                                sx={{ textTransform: 'none', color: '#1E7C88', fontWeight: 500 }}
                                onClick={() => {
                                    const visibleUserIds = getVisibleUsers().map((user) => user.user_id);
                                    const allSelected = visibleUserIds.every((id) => selectedUserIds.includes(id));
                                    if (allSelected) {
                                        setSelectedUserIds((prev) => prev.filter((id) => !visibleUserIds.includes(id)));
                                    } else {
                                        setSelectedUserIds((prev) => Array.from(new Set([...prev, ...visibleUserIds])));
                                    }
                                }}
                            >
                                {getVisibleUsers().every((user) => selectedUserIds.includes(user.user_id)) ? 'Unselect All' : 'Select All'}
                            </Button>
                        )}
                    </Box>

                    <Box
                        sx={{
                            maxHeight: '40vh',
                            overflowY: 'auto',
                            minHeight: '120px',
                            width: '100%',
                            height: '100%',
                            border: '1px solid #E0E0E0',
                            borderRadius: 1,
                            p: 1
                        }}
                        mt={2}
                        display="flex"
                        flexDirection="column"
                    >
                        {loadingUsers ? (
                            <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
                                <CircularProgress color="primary" />
                            </Box>
                        ) : getVisibleUsers().length === 0 ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                width="100%"
                                height="100%"
                                py={4}
                            >
                                <Typography color="textSecondary">No users found</Typography>
                            </Box>
                        ) : (
                            getVisibleUsers().map((item, index) => {
                                const isSelected = selectedUserIds.includes(item.user_id);
                                const handleUserToggle = () => {
                                    setSelectedUserIds((prev) =>
                                        prev.includes(item.user_id) ? prev.filter((id) => id !== item.user_id) : [...prev, item.user_id]
                                    );
                                };
                                return (
                                    <Box
                                        key={`${index}-users`}
                                        mb={1}
                                        onClick={handleUserToggle}
                                        sx={{
                                            cursor: 'pointer',
                                            flexWrap: 'wrap',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 1,
                                            borderRadius: 2,
                                            background: isSelected ? '#F4F4F5' : 'transparent',
                                            transition: 'background 0.2s',
                                            '&:hover': { background: '#F4F4F5' }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar src={item.profile_image} alt={item.first_name} />
                                            <Box display="flex" flexDirection="column">
                                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                    {item.first_name + ' ' + item.last_name}
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: '0.9rem' }, color: '#888' }}>
                                                    {item?.job_title && item.job_title.length > 0
                                                        ? item.job_title[0].title
                                                        : 'No Job Title found'}{' '}
                                                    | {item?.branch?.branch_name || 'No Branch found'} |{' '}
                                                    {item?.department?.name || 'No Department found'} |{' '}
                                                    {item?.department_divisions && item.department_divisions.length > 0
                                                        ? item.department_divisions[0].name
                                                        : 'No Division found'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {isSelected && (
                                            <Box component="img" src={tikcircle} alt="selected" sx={{ width: 32, height: 32 }} />
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {selectedUserIds.length > 0 && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={2}
                            mb={2}
                            sx={{
                                overflowX: 'auto',
                                width: '100%',
                                '&::-webkit-scrollbar': { height: '6px' },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#d1d1d1',
                                    borderRadius: '6px'
                                },
                                scrollbarWidth: 'thin',
                                flexWrap: 'nowrap'
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 'max-content' }}>
                                {getVisibleUsers()
                                    .filter((user) => selectedUserIds.includes(user.user_id))
                                    .map((user, index) => (
                                        <Box
                                            key={user.user_id}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            sx={{ cursor: 'pointer', transform: `translateX(${index * -20}px)` }}
                                        >
                                            <Avatar
                                                src={user.profile_image}
                                                alt={user.first_name}
                                                sx={{ width: 36, height: 36, border: '2px solid #1E7C88' }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    maxWidth: 60,
                                                    textAlign: 'center',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {user.user_name}
                                            </Typography>
                                        </Box>
                                    ))}
                            </Box>
                        </Box>
                    )}
                </CardContent>
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '100%',
                        p: 2,
                        backgroundColor: 'white',
                        borderTop: '1px solid #E0E0E0',
                        zIndex: 2
                    }}
                >
                    <Button fullWidth variant="contained" size="large" onClick={handleConfirm} disabled={selectedUserIds.length === 0}>
                        <Typography variant="h6">{buttonText || 'Compare Selected Users'}</Typography>
                    </Button>
                </Box>
            </Card>
        </Modal>
    );
};

export default UserSelectionModal;
